import type { ReactElement } from "react"
import { useState } from "react"
import AnimatedLinkIndicatorMol from "@onestore/hel/dist/components/molecules/AnimatedLinkIndicatorMol"
import ContentToggleMol from "@onestore/hel/dist/components/molecules/ContentToggleMol"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import BreakpointVisibilityOrg from "@onestore/hel/dist/components/organisms/BreakpointVisibilityOrg"
import ContentWithImageOrg from "@onestore/hel/dist/components/organisms/ContentWithImageOrg"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import SliderTemplate from "@onestore/hel/dist/components/templates/SliderTemplate"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import _t from "@core/i18n"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import type { LinksAnimatedImageSectionFlatData } from "@gatsby-plugin-generic-page/types/sectionType"
import { Picture, Text } from "@ionos-web-design-system/react"
import assetsSquidexToImageObject from "~/lib/assetsSquidexToImageObject"

export default function LinksAnimatedImageSection({
  title,
  linksWithImage,
  linksTitle,
  sectionSettings,
}: LinksAnimatedImageSectionFlatData): ReactElement<LinksAnimatedImageSectionFlatData> {
  const [activeLink, setActiveLink] = useState(0)
  const isSmallOnlyScreen = useBreakpoint("small", "medium")

  const imageContents = linksWithImage.map((links) => {
    const { image, text } = links

    return (
      <Picture
        key={`image-${text}`}
        srcData={assetsSquidexToImageObject(image, text)}
      />
    )
  })

  const slides = linksWithImage.map((links) => {
    const { image, text } = links

    return {
      title: text,
      image: <Picture srcData={assetsSquidexToImageObject(image, text)} />,
    }
  })

  const imagesJsx = (
    <ContentToggleMol activeIndex={activeLink} content={imageContents} />
  )

  const renderTitle = (isTitleAsHeading: boolean) =>
    title ? (
      isTitleAsHeading ? (
        <Text asChild variant="heading4xl" weight="normal" className="mb-12">
          <h2>{title}</h2>
        </Text>
      ) : (
        <Text
          variant="heading4xl"
          weight="normal"
          className="mb-12"
          role="heading"
          aria-level={2}
        >
          {title}
        </Text>
      )
    ) : undefined

  const contentJsx = (
    <div>
      {linksTitle ? (
        <Text variant="body" weight="normal" className="mb-4">
          {linksTitle}
        </Text>
      ) : null}

      {linksWithImage.map((link, index) => (
        <PushOrg bottomSpace={5} key={`link-${link.text}`}>
          <AnimatedLinkIndicatorMol
            isActive={activeLink === index}
            text={link.text}
            onClick={() => {
              setActiveLink(index)
            }}
          />
        </PushOrg>
      ))}
    </div>
  )

  return (
    <BaseSection hasOverflowHidden={isSmallOnlyScreen} {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        <BreakpointVisibilityOrg from="large">
          <ContentWithImageOrg
            gutter="medium"
            content={
              <>
                {renderTitle(false)}

                {contentJsx}
              </>
            }
            image={imagesJsx}
          />
        </BreakpointVisibilityOrg>

        <BreakpointVisibilityOrg from="medium" to="large">
          <PushOrg bottomSpace={5}>
            <AlignContentOrg horizontalAlign="center">
              {renderTitle(false)}

              {imagesJsx}
            </AlignContentOrg>
          </PushOrg>

          <FlexContainerOrg justifyContent="center">
            {contentJsx}
          </FlexContainerOrg>
        </BreakpointVisibilityOrg>

        <BreakpointVisibilityOrg from="small" to="medium">
          <PushOrg bottomSpace={5}>
            <AlignContentOrg horizontalAlign="center">
              {renderTitle(true)}
            </AlignContentOrg>
          </PushOrg>

          <SliderTemplate
            slides={slides}
            ratio="original"
            hasArrowsOnMobile
            slideTitle={{
              dot: _t("slider.dot"),
              active: _t("slider.active"),
              next: _t("slider.next"),
              previous: _t("slider.previous"),
              play: _t("slider.play"),
              pause: _t("slider.pause"),
            }}
          />
        </BreakpointVisibilityOrg>
      </BaseContainer>
    </BaseSection>
  )
}
